import { useState, useMemo, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Map as MapboxMap } from "mapbox-gl";
import { useRecordDeliveriesByAddresses, useUndoDeliveriesByAddresses } from "../../api/deliveries";
import { getCachedFeatures } from "../layers/utils";
import { markAddressesAsDelivered } from "../layers";
import type { AddressFeatureProperties } from "../../api/map/types";
import type { AddressResponse } from "../../api/addresses/types";
import type { DeliveryResponse } from "../../api/deliveries";
import { getCurrentSessionId } from "../utils/sessionManager";
import { AddressMapActionList, buildAddressGoogleMapsSearchUrl, canUndoAddressMapDelivery } from "./AddressMapActionList";
import "./BulkAddressDeliveryPanel.css";

export interface BulkAddressDeliveryPanelProps {
    campaignId: string;
    selectedAddressIds: string[];
    flyerTypeIds: string[];
    map?: MapboxMap | null;
    onClearSelection: () => void;
    // Props for single address operations (when selectedAddressIds.length === 1)
    singleAddress?: AddressResponse;
    onHide?: () => void;
    onBlock?: () => void;
    onMove?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
    onUndoDelivery?: () => void;
    onDeliveryHistory?: () => void;
    selectedFlyerTypeId?: string | null;
    deliveries?: DeliveryResponse[];
    currentUserId?: string;
}

export const BulkAddressDeliveryPanel = ({
    campaignId,
    selectedAddressIds,
    flyerTypeIds,
    map,
    onClearSelection,
    singleAddress,
    onHide,
    onBlock,
    onMove,
    onEdit,
    onCopy,
    onUndoDelivery,
    onDeliveryHistory,
    selectedFlyerTypeId,
    deliveries = [],
    currentUserId
}: BulkAddressDeliveryPanelProps): JSX.Element | null => {
    const { t } = useTranslation();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showAddressList, setShowAddressList] = useState(false);
    const [bottomOffset, setBottomOffset] = useState<string>("3.5rem");
    const panelRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const statusBar = document.querySelector(".map-status-bar");
        if (statusBar) {
            const height = statusBar.getBoundingClientRect().height;
            setBottomOffset(`${height + 16}px`);
        }
    }, []);

    const recordDeliveriesMutation = useRecordDeliveriesByAddresses();
    const undoDeliveriesMutation = useUndoDeliveriesByAddresses();

    // Get address information from map features
    const selectedAddresses = useMemo(() => {
        if (!map || selectedAddressIds.length === 0) {
            return [];
        }

        const allAddresses = getCachedFeatures<AddressFeatureProperties>(map, "addresses");
        const selectedSet = new Set(selectedAddressIds);

        return allAddresses
            .filter((feature) => {
                const id = feature.id ?? feature.properties.addressPointId;
                return id && selectedSet.has(String(id));
            })
            .map((feature) => {
                const props = feature.properties;
                const address = `${props.street}${props.number ? ` ${props.number}` : ""}${props.apartment ? ` ${props.apartment}` : ""}`;
                return {
                    id: props.addressPointId,
                    address,
                    city: props.city,
                    postalCode: props.postalCode
                };
            })
            .sort((a, b) => {
                // Sort by street name, then number
                if (a.address !== b.address) {
                    return a.address.localeCompare(b.address);
                }
                return a.city.localeCompare(b.city);
            });
    }, [map, selectedAddressIds]);

    if (selectedAddressIds.length === 0) {
        return null;
    }

    const handleMarkAsDelivered = () => {
        if (flyerTypeIds.length === 0) {
            // TODO: Show error - need to select flyer types
            return;
        }

        setShowConfirmDialog(true);
    };

    const handleConfirm = () => {
        // Optimistically update the map immediately
        if (map) {
            markAddressesAsDelivered(
                map,
                selectedAddressIds,
                flyerTypeIds
            );
        }

        recordDeliveriesMutation.mutate(
            {
                campaignId,
                addressPointIds: selectedAddressIds,
                flyerTypeIds,
                clientRequestId: getCurrentSessionId()
            },
            {
                onSuccess: () => {
                    setShowConfirmDialog(false);
                    onClearSelection();
                },
                onError: () => {
                    // Keep dialog open on error
                }
            }
        );
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const isLoading = recordDeliveriesMutation.isPending || undoDeliveriesMutation.isPending;

    const canUndo = canUndoAddressMapDelivery(selectedFlyerTypeId, onUndoDelivery, deliveries, currentUserId);

    const isSingleAddress = selectedAddressIds.length === 1;

    return (
        <>
            <div ref={panelRef} className="bulk-address-panel" style={{ bottom: bottomOffset }}>
                <div className="bulk-address-panel__header">
                    {isSingleAddress && singleAddress ? (
                        <>
                            <div className="bulk-address-panel__address-info">
                                <h3 className="bulk-address-panel__title">
                                    <strong>{singleAddress.street}</strong>
                                    {singleAddress.number && <span> {singleAddress.number}</span>}
                                    {singleAddress.apartment && <span> {singleAddress.apartment}</span>}
                                </h3>
                                <div className="bulk-address-panel__address-details">
                                    {singleAddress.postalCode} {singleAddress.city}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="bulk-address-panel__clear-button"
                                onClick={onClearSelection}
                                disabled={isLoading}
                                aria-label={t("map.bulkAddress.clearSelection")}
                            >
                                ✕
                            </button>
                        </>
                    ) : (
                        <>
                            <h3 className="bulk-address-panel__title">
                                {t("map.bulkAddress.selected", { count: selectedAddressIds.length })}
                            </h3>
                            <button
                                type="button"
                                className="bulk-address-panel__clear-button"
                                onClick={onClearSelection}
                                disabled={isLoading}
                                aria-label={t("map.bulkAddress.clearSelection")}
                            >
                                ✕
                            </button>
                        </>
                    )}
                </div>
                {!isSingleAddress && (
                    <div className="bulk-address-panel__summary">
                        <button
                            type="button"
                            className="bulk-address-panel__toggle-list"
                            onClick={() => setShowAddressList(!showAddressList)}
                            aria-expanded={showAddressList}
                        >
                            {showAddressList ? t("map.bulkAddress.hideList") : t("map.bulkAddress.showList")}
                            <span className="bulk-address-panel__toggle-icon">
                                {showAddressList ? "▼" : "▶"}
                            </span>
                        </button>
                    </div>
                )}

                {!isSingleAddress && showAddressList && selectedAddresses.length > 0 && (
                    <div className="bulk-address-panel__address-list">
                        <ul className="bulk-address-panel__address-items">
                            {selectedAddresses.map((addr) => (
                                <li key={addr.id} className="bulk-address-panel__address-item">
                                    <span className="bulk-address-panel__address-text">
                                        {addr.address}
                                    </span>
                                    <span className="bulk-address-panel__address-meta">
                                        {addr.postalCode} {addr.city}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Context menu actions for single address */}
                {isSingleAddress && singleAddress && (
                    <div className="bulk-address-panel__context-actions">
                        <div className="bulk-address-panel__context-divider" />
                        <div className="bulk-address-panel__context-buttons">
                            <AddressMapActionList
                                variant="panel"
                                address={singleAddress}
                                googleMapsUrl={buildAddressGoogleMapsSearchUrl(singleAddress)}
                                isLoading={isLoading}
                                markDeliveredDisabled={flyerTypeIds.length === 0}
                                canUndo={Boolean(canUndo)}
                                dismissAfterAction={onClearSelection}
                                keepHostOpenOnDeliveryHistory
                                dismissAfterMarkDelivered={false}
                                onDeliveryHistory={onDeliveryHistory}
                                onMarkDelivered={handleMarkAsDelivered}
                                onUndoDelivery={canUndo ? onUndoDelivery : undefined}
                                onHide={onHide}
                                onBlock={onBlock}
                                onMove={onMove}
                                onEdit={onEdit}
                                onCopy={onCopy}
                            />
                        </div>
                    </div>
                )}

                {/* Bulk delivery action */}
                {!isSingleAddress && (
                    <div className="bulk-address-panel__actions">
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--primary"
                            onClick={handleMarkAsDelivered}
                            disabled={isLoading || flyerTypeIds.length === 0}
                        >
                            {isLoading
                                ? t("common.loading")
                                : t("map.bulkAddress.markAsDelivered")}
                        </button>
                    </div>
                )}
            </div>

            {showConfirmDialog && (
                <div className="bulk-address-panel__overlay" onClick={handleCancel}>
                    <div
                        className="bulk-address-panel__dialog"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-labelledby="bulk-address-dialog__title"
                        aria-modal="true"
                    >
                        <header className="bulk-address-panel__dialog-header">
                            <h2 id="bulk-address-dialog__title" className="bulk-address-panel__dialog-title">
                                {t("map.bulkAddress.confirmTitle")}
                            </h2>
                        </header>
                        <div className="bulk-address-panel__dialog-body">
                            <p>
                                {t("map.bulkAddress.confirmMessage", {
                                    count: selectedAddressIds.length
                                })}
                            </p>
                            {selectedAddresses.length > 0 && selectedAddresses.length <= 20 && (
                                <div className="bulk-address-panel__dialog-address-list">
                                    <strong>{t("map.bulkAddress.selectedAddresses")}:</strong>
                                    <ul className="bulk-address-panel__dialog-address-items">
                                        {selectedAddresses.map((addr) => (
                                            <li key={addr.id}>
                                                {addr.address}, {addr.postalCode} {addr.city}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {selectedAddresses.length > 20 && (
                                <p className="bulk-address-panel__dialog-note">
                                    {t("map.bulkAddress.tooManyToShow", { count: selectedAddresses.length })}
                                </p>
                            )}
                        </div>
                        <footer className="bulk-address-panel__dialog-footer">
                            <button
                                type="button"
                                className="bulk-address-panel__button bulk-address-panel__button--cancel"
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="button"
                                className="bulk-address-panel__button bulk-address-panel__button--confirm"
                                onClick={handleConfirm}
                                disabled={isLoading}
                            >
                                {isLoading ? t("common.loading") : t("common.confirm")}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </>
    );
};